import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import {
  EFormBlockBackground,
  EFormBlockMediaLayout,
  getFormBlockTint,
  getTextBlocks,
  type TFormTextBlock,
} from '@onesource/schemas';
import { getMediaUrl, type TFormQuestion } from '@onesource/services';
import { sanitizeRichHtml } from '@onesource/utils';
import { useState, type KeyboardEvent } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';

import { FORM_CONTENT_MAX_WIDTH, formRichContentSx } from './rich-content-sx';

interface TextBlockQuestionProps {
  question: TFormQuestion;
}

const bodySx = {
  fontSize: '0.9375rem',
  lineHeight: 1.65,
  color: 'text.primary',
  // Paragraphs carry the rhythm here, so they get real spacing — tighter than the
  // gap between blocks so the grouping still reads.
  '& p': { m: 0, mb: 2 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { pl: 3, m: 0, mb: 2 },
  '& a': { color: '#ff4d6d' },
  ...formRichContentSx,
} as const;

const BlockMedia = ({ media, headline }: { media: NonNullable<TFormTextBlock['media']>; headline: string }) => (
  <Box
    component="img"
    src={getMediaUrl(media.url)}
    alt={media.alt || headline || ''}
    sx={{ width: '100%', display: 'block', borderRadius: '12px', objectFit: 'cover' }}
  />
);

/**
 * Read-only content inside a form: one or more headline + body blocks, each
 * optionally tinted as a callout and optionally carrying an image.
 *
 * Takes no answer and reports no value — a workbook uses these to carry the prose
 * between the prompts, so it must not appear in progress counts or validation.
 *
 * Left-aligned on a narrower measure than the page, unlike the centred single-line
 * question text: these are paragraphs to read, and centred prose at this length is
 * hard work.
 */
export const TextBlockQuestion = ({ question }: TextBlockQuestionProps) => {
  const blocks = getTextBlocks(question.metadata);
  // Blocks the reader has folded away. Tracked as the collapsed set so a block is
  // open until they choose otherwise — instructions must never start hidden.
  const [collapsedIds, setCollapsedIds] = useState<string[]>([]);

  const toggleCollapsed = (blockId: string) =>
    setCollapsedIds(prev => (prev.includes(blockId) ? prev.filter(id => id !== blockId) : [...prev, blockId]));

  if (blocks.length === 0) {
    return null;
  }

  return (
    <Box sx={{ maxWidth: FORM_CONTENT_MAX_WIDTH, mx: 'auto', textAlign: 'left' }}>
      {blocks.map((block, index) => {
        const tint = getFormBlockTint(block.background);
        const isCallout = block.background !== EFormBlockBackground.NONE;
        // A named preset ("Pause & Reflect") prints its own label when the admin
        // left the headline blank, so the callout still announces what it is.
        const headline = block.headline || tint.defaultHeadline;
        const hasBar = tint.bar !== 'transparent';
        const isBeside = block.mediaLayout === EFormBlockMediaLayout.BESIDE && !!block.media;
        // Only a headlined block with something under it can fold: the headline is
        // what stays behind as the handle to open it again, and a callout that is
        // just a coloured label (its prompts are the questions below it) has
        // nothing to collapse — offering a toggle there does nothing.
        const isCollapsible = !!headline && (!!block.body?.trim() || !!block.media);
        const isCollapsed = isCollapsible && collapsedIds.includes(block.id);

        const text = block.body ? (
          <Box sx={bodySx} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(block.body) }} />
        ) : null;

        return (
          <Box
            key={block.id}
            sx={{
              mb: index === blocks.length - 1 ? 0 : 4,
              ...(isCallout && {
                bgcolor: tint.bg,
                p: 2,
                // Named presets are drawn as the workbook draws them: a solid
                // accent bar down the left and square corners on that edge, so the
                // bar reads as part of the panel rather than a stripe beside it.
                ...(hasBar
                  ? {
                      borderLeft: '3px solid',
                      borderLeftColor: tint.bar,
                      borderRadius: '0 8px 8px 0',
                    }
                  : { border: '1px solid', borderColor: tint.border, borderRadius: '10px' }),
              }),
            }}
          >
            {headline ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: isCollapsed ? 0 : 1.5,
                  ...(isCollapsible && { cursor: 'pointer' }),
                }}
                {...(isCollapsible && {
                  role: 'button',
                  tabIndex: 0,
                  'aria-expanded': !isCollapsed,
                  onClick: () => toggleCollapsed(block.id),
                  onKeyDown: (event: KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggleCollapsed(block.id);
                    }
                  },
                })}
              >
                <Typography
                  component="h3"
                  sx={
                    // Inside a named callout the headline is the workbook's small
                    // uppercase tag, not a heading; a plain block keeps the larger
                    // heading it always had.
                    hasBar
                      ? {
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          letterSpacing: 0.8,
                          textTransform: 'uppercase',
                          lineHeight: 1.4,
                          color: tint.ink,
                          flex: 1,
                        }
                      : { fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.4, color: 'common.black', flex: 1 }
                  }
                >
                  {headline}
                </Typography>
                {isCollapsible && (
                  <Box component="span" sx={{ display: 'flex', color: 'text.secondary', flexShrink: 0 }}>
                    {isCollapsed ? <MdExpandMore size={22} /> : <MdExpandLess size={22} />}
                  </Box>
                )}
              </Box>
            ) : null}

            <Collapse in={!isCollapsed}>
              {isBeside ? (
                // Side by side on desktop, stacked on mobile — at phone widths a
                // half-width column of prose is unreadable.
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 3,
                    alignItems: 'start',
                  }}
                >
                  <Box>{text}</Box>
                  <BlockMedia media={block.media!} headline={headline} />
                </Box>
              ) : (
                <>
                  {text}
                  {block.media ? (
                    <Box sx={{ mt: 3 }}>
                      <BlockMedia media={block.media} headline={headline} />
                    </Box>
                  ) : null}
                </>
              )}
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};
